import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Fab,
  List,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { toyService } from '../../services/toyService';
import { currencyService } from '../../services/currencyService';
import { useTranslation } from '../../hooks/useTranslation';
import { publicCollectionService } from '../../services/publicCollectionService';

// Import smaller components
import ToyListControls from './ToyListControls';
import ToyCard from './ToyCard';
import ToyListItem from './ToyListItem';
import ToyListFooter from './ToyListFooter';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import CollectionBanner from '../layout/CollectionBanner';

function ToyList() {
  const [allToys, setAllToys] = useState([]);
  const [displayedToys, setDisplayedToys] = useState([]);
  const [filteredToys, setFilteredToys] = useState([]);
  const [searchTerm, setSearchTerm] = useState(() => {
    return localStorage.getItem('toyport_searchTerm') || '';
  });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [toyToDelete, setToyToDelete] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('toyport_viewMode') || 'grid';
  });
  const [userCurrency, setUserCurrency] = useState('USD');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [shareFeedback, setShareFeedback] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const ITEMS_PER_PAGE = 20;
  
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Helper function to get image URL with fallback
  const getImageUrl = (imageUrl, toyName) => {
    if (imageUrl) {
      return imageUrl;
    }
    const encodedName = encodeURIComponent(toyName || 'Toy');
    return `https://placehold.co/400x300/e0e0e0/666666?text=${encodedName}`;
  };

  useEffect(() => {
    loadToys();
    loadUserCurrency();
    const savedScrollPosition = localStorage.getItem('toyport_scrollPosition');
    if (savedScrollPosition) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScrollPosition));
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem('toyport_searchTerm', searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    localStorage.setItem('toyport_viewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
      
      if (
        !loadingMore &&
        hasMore &&
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1000
      ) {
        loadMoreItems();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMore, hasMore, filteredToys.length, currentPage]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = allToys.filter(toy =>
        toy.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        toy.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        toy.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        toy.series?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredToys(filtered);
      setCurrentPage(1);
      setDisplayedToys(filtered.slice(0, ITEMS_PER_PAGE));
      setHasMore(filtered.length > ITEMS_PER_PAGE);
    } else {
      setFilteredToys(allToys);
      setCurrentPage(1);
      setDisplayedToys(allToys.slice(0, ITEMS_PER_PAGE));
      setHasMore(allToys.length > ITEMS_PER_PAGE);
    }
  }, [searchTerm, allToys]);

  useEffect(() => {
    if (!shareFeedback || shareFeedback.severity === 'info') return;
    const timeout = setTimeout(() => setShareFeedback(null), 4000);
    return () => clearTimeout(timeout);
  }, [shareFeedback]);

  async function loadToys() {
    if (!currentUser?.uid) return;
    
    try {
      setLoading(true);
      const toysData = await toyService.getToys(currentUser.uid);
      setAllToys(toysData);
      setFilteredToys(toysData);
      setDisplayedToys(toysData.slice(0, ITEMS_PER_PAGE));
      setHasMore(toysData.length > ITEMS_PER_PAGE);
      setCurrentPage(1);
    } catch (error) {
      setError('Failed to load toys: ' + error.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadMoreItems() {
    if (loadingMore || !hasMore) return;
    
    try {
      setLoadingMore(true);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const nextPage = currentPage + 1;
      const startIndex = (nextPage - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const newItems = filteredToys.slice(startIndex, endIndex);
      
      setDisplayedToys(prev => [...prev, ...newItems]);
      setCurrentPage(nextPage);
      setHasMore(endIndex < filteredToys.length);
    } catch (error) {
      console.error('Failed to load more items:', error);
    } finally {
      setLoadingMore(false);
    }
  }

  async function loadUserCurrency() {
    if (!currentUser?.uid) return;
    
    try {
      const currency = await currencyService.getUserCurrency(currentUser.uid);
      setUserCurrency(currency);
    } catch (error) {
      console.error('Failed to load user currency:', error);
    }
  }

  async function handleDelete(toyId) {
    if (!currentUser?.uid) {
      setError('User not authenticated');
      return;
    }
    
    if (!toyId) {
      setError('Invalid toy ID');
      return;
    }
    
    try {
      await toyService.deleteToy(currentUser.uid, toyId);
      setAllToys(prevToys => prevToys.filter(toy => toy.id !== toyId));
      setFilteredToys(prevToys => prevToys.filter(toy => toy.id !== toyId));
      setDisplayedToys(prevToys => prevToys.filter(toy => toy.id !== toyId));
      setError(''); // Clear any existing errors
    } catch (error) {
      console.error('Delete toy error:', error);
      setError('Failed to delete toy: ' + error.message);
    } finally {
      // Always close the dialog and reset state
      setDeleteDialogOpen(false);
      setToyToDelete(null);
    }
  }

  async function handleClone(toy) {
    if (!currentUser?.uid) return;
    
    try {
      // Create a clone of the toy data, removing the id and timestamps
      const clonedToy = {
        name: `${toy.name} (Copy)`,
        brand: toy.brand || '',
        code: toy.code || '',
        series: toy.series || '',
        type: toy.type || '',
        scale: toy.scale || '',
        imageUrl: toy.imageUrl || '',
        qty: toy.qty || 1,
        price: toy.price || 0,
        preorder_price: toy.preorder_price || 0,
        source: toy.source || '',
        note: toy.note || '-',
        createdDate: toy.createdDate || ''
      };

      // Save the cloned toy
      await toyService.createToy(clonedToy, currentUser.uid);
      
      // Reload toys to show the new clone
      await loadToys();
      
      // Show success message
      setError(''); // Clear any existing errors
      // You could add a success state here if needed
    } catch (error) {
      setError('Failed to clone toy: ' + error.message);
    }
  }

  function handleEditToy(toyId) {
    localStorage.setItem('toyport_scrollPosition', window.scrollY.toString());
    navigate(`/toy/${toyId}`);
  }

  function handleAddNewToy() {
    localStorage.setItem('toyport_scrollPosition', window.scrollY.toString());
    navigate('/toy/new');
  }

  function handleGoToInsights() {
    navigate('/insights');
  }

  async function publishPublicSnapshot() {
    if (!currentUser?.uid) return false;
    if (shareLoading) return true;

    try {
      setShareLoading(true);
      await publicCollectionService.publishCollection(currentUser.uid, allToys, userCurrency);
      return true;
    } catch (publishError) {
      console.error('Failed to publish public collection snapshot:', publishError);
      setShareFeedback({
        severity: 'error',
        message: t('collection.share.publishError')
      });
      return false;
    } finally {
      setShareLoading(false);
    }
  }

  async function handleShareLinkCopy() {
    if (!currentUser?.uid) return;

    if (allToys.length === 0) {
      setShareFeedback({
        severity: 'info',
        message: t('collection.share.nothingToShare')
      });
      return;
    }

    const snapshotPublished = await publishPublicSnapshot();
    if (!snapshotPublished) {
      return;
    }

    const publicUrl = `${window.location.origin}/public/${currentUser.uid}`;

    if (!navigator?.clipboard) {
      setShareFeedback({
        severity: 'info',
        message: t('collection.share.copyFailed', { url: publicUrl })
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(publicUrl);
      setShareFeedback({
        severity: 'success',
        message: t('collection.share.copied')
      });
    } catch (copyError) {
      console.error('Failed to copy public link:', copyError);
      setShareFeedback({
        severity: 'info',
        message: t('collection.share.copyFailed', { url: publicUrl })
      });
    }
  }

  async function handleOpenPublicPage() {
    if (!currentUser?.uid) return;

    if (allToys.length === 0) {
      setShareFeedback({
        severity: 'info',
        message: t('collection.share.nothingToShare')
      });
      return;
    }

    const snapshotPublished = await publishPublicSnapshot();
    if (!snapshotPublished) {
      return;
    }

    const publicUrl = `${window.location.origin}/public/${currentUser.uid}`;
    window.open(publicUrl, '_blank', 'noopener,noreferrer');
  }

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function openDeleteDialog(toy) {
    setToyToDelete(toy);
    setDeleteDialogOpen(true);
  }

  function handleSearchChange(e) {
    setSearchTerm(e.target.value);
  }

  function handleClearSearch() {
    setSearchTerm('');
  }

  function handleViewModeChange(mode) {
    setViewMode(mode);
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Collection Banner */}
      <CollectionBanner 
        onInsightsClick={handleGoToInsights}
        onAddToyClick={handleAddNewToy}
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {shareFeedback && (
          <Alert
            severity={shareFeedback.severity}
            onClose={() => setShareFeedback(null)}
            sx={{ mb: 3 }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {shareFeedback.message}
            </Typography>
          </Alert>
        )}

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          sx={{ mb: 3 }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('collection.share.description')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleShareLinkCopy}
              disabled={loading || shareLoading}
            >
              {shareLoading ? t('collection.share.publishing') : t('collection.share.copyLink')}
            </Button>
            <Button
              variant="text"
              startIcon={<OpenInNewIcon />}
              onClick={handleOpenPublicPage}
              disabled={loading || shareLoading}
            >
              {t('collection.share.preview')}
            </Button>
          </Stack>
        </Stack>

        <ToyListControls
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onClearSearch={handleClearSearch}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          filteredToys={filteredToys}
          userCurrency={userCurrency}
        />

        {/* Toys Grid/List */}
        {displayedToys.length === 0 ? (
          <Box sx={{
            background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
            p: 6,
            borderRadius: 3,
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              {searchTerm ? t('collection.noSearchResults') : t('collection.noToys')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {searchTerm ? 'Try adjusting your search terms.' : 'Start building your collection by adding your first toy!'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNewToy}
              size="large"
            >
              {t('collection.addFirstToy')}
            </Button>
          </Box>
        ) : (
          <>
            {viewMode === 'grid' ? (
              <Grid container spacing={3}>
                {displayedToys.map((toy) => (
                  <ToyCard
                    key={toy.id}
                    toy={toy}
                    userCurrency={userCurrency}
                    onEdit={handleEditToy}
                    onDelete={openDeleteDialog}
                    onClone={handleClone}
                    getImageUrl={getImageUrl}
                  />
                ))}
              </Grid>
            ) : (
              <List>
                {displayedToys.map((toy) => (
                  <ToyListItem
                    key={toy.id}
                    toy={toy}
                    userCurrency={userCurrency}
                    onEdit={handleEditToy}
                    onDelete={openDeleteDialog}
                    onClone={handleClone}
                    getImageUrl={getImageUrl}
                  />
                ))}
              </List>
            )}

            {/* Loading More Indicator */}
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress size={40} />
              </Box>
            )}

            {/* End of Results Indicator */}
            {!hasMore && displayedToys.length > 0 && (
              <Box sx={{ textAlign: 'center', mt: 4, py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('collection.endOfResults')}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Container>

      <ToyListFooter />

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <Fab
          color="primary"
          aria-label="scroll to top"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      )}

      {/* Floating Add Button for Mobile */}
      <Fab
        color="primary"
        aria-label="add new toy"
        onClick={handleAddNewToy}
        sx={{
          position: 'fixed',
          bottom: showScrollTop ? 80 : 16,
          right: 16,
          zIndex: 1000,
          display: { xs: 'flex', md: 'none' } // Only show on mobile
        }}
      >
        <AddIcon />
      </Fab>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => {
          if (toyToDelete?.id) {
            handleDelete(toyToDelete.id);
          }
        }}
      />
    </Box>
  );
}

export default ToyList; 
